<!-- 侧边菜单栏菜单 -->
<template>
  <!-- 有子菜单的情况 -->
  <el-sub-menu
    v-if="hasChildren"
    :index="menuIndex"
  >
    <template #title>
      <!-- 菜单图标 -->
      <el-icon v-if="item.meta?.icon" class="menu-icon">
        <component :is="item.meta.icon" />
      </el-icon>
      <!-- 菜单标题 - 使用 v-show 代替 v-if 实现过渡 -->
      <span class="menu-title" v-show="!isCollapse">
        {{ item.meta?.title || item.name }}
      </span>
    </template>
    <!-- 递归渲染子菜单 -->
    <SidebarMenuItem
      v-for="child in visibleChildren"
      :key="child.path"
      :item="child"
      :parent-path="menuIndex"
      :is-collapse="isCollapse"
      :level="level + 1"
      :active-menu="activeMenu"
      @menu-click="$emit('menu-click', $event)"
    />
  </el-sub-menu>

  <!-- 没有子菜单的情况 -->
  <el-menu-item
    v-else
    :index="menuIndex"
    :class="{ 'is-active': activeMenu === menuIndex }"
    @click="handleMenuItemClick"
  >
    <!-- 菜单图标 -->
    <el-icon v-if="item.meta?.icon" class="menu-icon">
      <component :is="item.meta.icon" />
    </el-icon>
    <!-- 菜单标题 - 使用 v-show 代替 v-if 实现过渡 -->
    <template #title>
      <span class="menu-title" v-show="!isCollapse">
        {{ item.meta?.title || item.name }}
      </span>
      <!-- 角标 -->
      <span v-if="item.meta?.badge" class="menu-badge" v-show="!isCollapse">
        {{ item.meta.badge }}
      </span>
      <!-- 外部链接图标 -->
      <el-icon v-if="item.meta?.external" class="external-icon" v-show="!isCollapse">
        <Link />
      </el-icon>
    </template>
  </el-menu-item>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Link } from '@element-plus/icons-vue'
import type { MenuItem } from '@/api'

interface Props {
  item: MenuItem
  parentPath?: string
  isCollapse?: boolean
  level?: number
  activeMenu?: string
}

interface Emits {
  (e: 'menu-click', path: string): void
}

const props = withDefaults(defineProps<Props>(), {
  parentPath: '',
  isCollapse: false,
  level: 1,
  activeMenu: ''
})

const emit = defineEmits<Emits>()

// 判断是否有子菜单
const hasChildren = computed(() => {
  const children = props.item.children || []
  const visibleChildren = children.filter(child => !child.meta?.hidden)
  return visibleChildren.length > 0
})

// 过滤隐藏的子菜单
const visibleChildren = computed(() => {
  return props.item.children?.filter(child => !child.meta?.hidden) || []
})

// 计算菜单的完整路径（带 /layout 前缀）- 用于 index 属性
const menuIndex = computed(() => {
  if (props.item.fullPath) {
    return props.item.fullPath.startsWith('/layout')
      ? props.item.fullPath
      : `/layout${props.item.fullPath}`
  }
  const basePath = props.parentPath || ''
  const itemPath = props.item.path || ''
  let fullPath = ''
  if (basePath === '' || basePath === '/') fullPath = itemPath.startsWith('/') ? itemPath : `/${itemPath}`
  else {
    fullPath = itemPath.startsWith('/')
      ? itemPath
      : (basePath.endsWith('/') ? `${basePath}${itemPath}` : `${basePath}/${itemPath}`)
  }
  if (!fullPath.startsWith('/layout')) fullPath = `/layout${fullPath}`
  return fullPath
})

// 处理菜单项点击
const handleMenuItemClick = () => {
  emit('menu-click', menuIndex.value)
}
</script>

<style scoped lang="scss">
.menu-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.2s ease, width 0.2s ease;
}

.menu-badge {
  display: inline-block;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  font-size: 12px;
  font-weight: normal;
  color: #fff;
  background-color: #f56c6c;
  border-radius: 9px;
  text-align: center;
  margin-left: 4px;
  transition: opacity 0.2s ease, width 0.2s ease;
}

.external-icon {
  font-size: 12px;
  margin-left: 4px;
  opacity: 0.7;
  transition: opacity 0.2s ease, width 0.2s ease;
}

// 折叠时隐藏文字和角标，只保留图标
.el-menu--collapse {
  .menu-title,
  .menu-badge,
  .external-icon {
    display: none !important;
  }
}

// 覆盖：弹出菜单中的文字要显示
.el-menu--popup .menu-title {
  display: block !important;
  color: #304156 !important;
}
</style>
